import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Badge, Card, Spinner, Alert } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import api from "../../utils/api";
import { fetchCurrentUser } from "../../../redux/userSlice";
import "./userdash.css";

const Withdrawal = () => {
	const dispatch = useDispatch();
	const { user } = useSelector((state) => state.user);
	const [amount, setAmount] = useState("");
	const [bankDetails, setBankDetails] = useState({
		accountNumber: "",
		confirmAccountNumber: "",
		ifscCode: "",
		bankName: ""
	});
	const [loading, setLoading] = useState(false);
	const [withdrawals, setWithdrawals] = useState([]);
	const [message, setMessage] = useState(null);

	const fetchMyWithdrawals = async () => {
		try {
			const res = await api.get("/withdrawals/my");
			setWithdrawals(res.data.withdrawals);
		} catch (error) {
			console.error("Error fetching withdrawals", error);
		}
	};

	useEffect(() => {
		fetchMyWithdrawals();
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setBankDetails(prev => ({ ...prev, [name]: value }));
	};

	const handleWithdrawal = async (e) => {
		e.preventDefault();

		if (bankDetails.accountNumber !== bankDetails.confirmAccountNumber) {
			setMessage({ type: "danger", text: "Account numbers do not match!" });
			return;
		}

		if (Number(amount) > user?.wallet?.balance) {
			setMessage({ type: "danger", text: "Insufficient balance!" });
			return;
		}

		setLoading(true);
		setMessage(null);
		try {
			const res = await api.post("/withdrawals/request", {
				amount: Number(amount),
				bankDetails: {
					accountNumber: bankDetails.accountNumber,
					ifscCode: bankDetails.ifscCode,
					bankName: bankDetails.bankName
				}
			});
			if (res.data.success) {
				setMessage({ type: "success", text: "Withdrawal request submitted! Credits deducted." });
				setAmount("");
				setBankDetails({
					accountNumber: "",
					confirmAccountNumber: "",
					ifscCode: "",
					bankName: ""
				});
				fetchMyWithdrawals();
				dispatch(fetchCurrentUser()); // Refresh balance
			}
		} catch (error) {
			setMessage({ type: "danger", text: error.response?.data?.message || "Withdrawal failed" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<Container className="my-4">
			<Row>
				<Col md={12}>
					<Card className="bg-dark text-white border-secondary mb-4">
						<Card.Header className="border-secondary d-flex justify-content-between align-items-center">
							<h4 className="mb-0">Request Withdrawal</h4>
							<h5 className="mb-0 text-warning">Balance: ₹{user?.wallet?.balance?.toFixed(2)}</h5>
						</Card.Header>
						<Card.Body>
							<Form onSubmit={handleWithdrawal}>
								<Row>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>Bank Name</Form.Label>
											<Form.Control
												type="text"
												name="bankName"
												placeholder="Enter bank name"
												value={bankDetails.bankName}
												onChange={handleChange}
												className="bg-secondary text-white border-0"
												required
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>IFSC Code</Form.Label>
											<Form.Control
												type="text"
												name="ifscCode"
												placeholder="Enter IFSC code"
												value={bankDetails.ifscCode}
												onChange={handleChange}
												className="bg-secondary text-white border-0"
												required
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>Account Number</Form.Label>
											<Form.Control
												type="text"
												name="accountNumber"
												placeholder="Enter account number"
												value={bankDetails.accountNumber}
												onChange={handleChange}
												className="bg-secondary text-white border-0"
												required
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>Confirm Account Number</Form.Label>
											<Form.Control
												type="text"
												name="confirmAccountNumber"
												placeholder="Confirm account number"
												value={bankDetails.confirmAccountNumber}
												onChange={handleChange}
												className="bg-secondary text-white border-0"
												required
											/>
										</Form.Group>
									</Col>
									<Col md={6}>
										<Form.Group className="mb-3">
											<Form.Label>Amount (Minimum ₹100)</Form.Label>
											<Form.Control
												type="number"
												placeholder="Enter amount"
												value={amount}
												onChange={(e) => setAmount(e.target.value)}
												className="bg-secondary text-white border-0"
												min="100"
												required
											/>
										</Form.Group>
									</Col>
									<Col md={6} className="d-flex align-items-end">
										<Button
											type="submit"
											variant="warning"
											className="w-100 fw-bold mb-3"
											disabled={loading}
										>
											{loading ? <Spinner animation="border" size="sm" /> : "Initiate Withdrawal"}
										</Button>
									</Col>
								</Row>
							</Form>
							{message && <Alert variant={message.type} className="mt-2">{message.text}</Alert>}
						</Card.Body>
					</Card>
				</Col>

				<Col md={12}>
					<Card className="bg-dark text-white border-secondary">
						<Card.Header className="border-secondary">
							<h4 className="mb-0">Withdrawal History</h4>
						</Card.Header>
						<Card.Body className="p-0">
							<Table responsive variant="dark" hover className="mb-0">
								<thead>
									<tr>
										<th>Date</th>
										<th>Bank Details</th>
										<th>Amount</th>
										<th>Status</th>
										<th>Remark</th>
									</tr>
								</thead>
								<tbody>
									{withdrawals.length === 0 ? (
										<tr>
											<td colSpan="5" className="text-center py-4">No withdrawals found</td>
										</tr>
									) : (
										withdrawals.map((w) => (
											<tr key={w._id}>
												<td className="small">{new Date(w.createdAt).toLocaleString()}</td>
												<td className="small">
													<div>{w.bankDetails.bankName}</div>
													<div>{w.bankDetails.accountNumber}</div>
													<div>{w.bankDetails.ifscCode}</div>
												</td>
												<td className="fw-bold">₹{w.amount}</td>
												<td>
													<Badge bg={w.status === "approved" ? "success" : w.status === "pending" ? "warning" : "danger"}>
														{w.status.toUpperCase()}
													</Badge>
												</td>
												<td className="small">{w.remark || "-"}</td>
											</tr>
										))
									)}
								</tbody>
							</Table>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Container>
	);
};

export default Withdrawal;
